// Progressive enhancement. Everything here is optional — the forms work without JS.
import { mountStraighten } from './photo-straighten';
import { openCamera } from './camera';
import { setupPWA } from './pwa';

interface SpreadData { id: string; system: string; positions: { position_index: number; label: string }[]; }
interface CardData { id: number; name: string; suit: string; system: string; symmetric?: boolean; }

// How the picker is grouped, per system (mirrors theme.ts CARD_GROUPS).
const SYSTEM_GROUPS: Record<string, { label: string; suit: string }[]> = {
  tarot: [
    { label: 'Major Arcana', suit: 'major' },
    { label: 'Wands', suit: 'wands' },
    { label: 'Cups', suit: 'cups' },
    { label: 'Swords', suit: 'swords' },
    { label: 'Pentacles', suit: 'pentacles' },
  ],
  lenormand: [{ label: 'Lenormand', suit: 'major' }],
  runes: [{ label: 'Runes', suit: 'major' }],
};

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[m] as string));
}

function cardOptionsHTML(cards: CardData[], system: string): string {
  let html = '<option value="">Tap to choose…</option>';
  for (const g of SYSTEM_GROUPS[system] || SYSTEM_GROUPS.tarot) {
    const inGroup = cards.filter((c) => c.system === system && c.suit === g.suit);
    if (!inGroup.length) continue;
    html += `<optgroup label="${esc(g.label)}">`;
    for (const c of inGroup) html += `<option value="${c.id}">${esc(c.name)}</option>`;
    html += '</optgroup>';
  }
  return html;
}

function setupReadingForm() {
  const form = document.getElementById('reading-form') as HTMLFormElement;
  if (!form) return;
  let spreads: SpreadData[] = [];
  let cards: CardData[] = [];
  let initial: Record<string, { c: number; r: number }> = {};
  try { spreads = JSON.parse(form.dataset.spreads || '[]'); cards = JSON.parse(form.dataset.cards || '[]'); } catch {}
  try { initial = JSON.parse(form.dataset.initial || '{}'); } catch {}
  const positionsEl = document.getElementById('positions');
  const cardById = new Map(cards.map((c) => [String(c.id), c]));

  const currentSystem = () => (form.querySelector('input[name="system"]:checked') as HTMLInputElement | null)?.value || 'tarot';

  // Runes can't be reversed if symmetric; non-symmetric show "Merkstave". Update a
  // position row's reverse toggle to match the chosen card + system.
  function refreshRune(select: HTMLSelectElement) {
    const row = select.closest('.pos-row');
    if (!row) return;
    const toggle = row.querySelector('.rev-toggle') as HTMLElement | null;
    const box = row.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
    const up = row.querySelector('.lbl.up') as HTMLElement | null;
    const rv = row.querySelector('.lbl.rv') as HTMLElement | null;
    if (!toggle || !box || !up || !rv) return;
    const card = cardById.get(select.value);
    const isRune = currentSystem() === 'runes';
    rv.textContent = isRune ? 'Merkstave' : 'Reversed';
    if (isRune && card?.symmetric) {
      box.checked = false; box.disabled = true; toggle.style.opacity = '0.4'; toggle.style.cursor = 'default';
      up.textContent = 'Symmetric';
    } else {
      box.disabled = false; toggle.style.opacity = ''; toggle.style.cursor = '';
      up.textContent = 'Upright';
    }
  }

  const posRowHTML = (label: string, index: number, opts: string) => `
      <div class="pos-row">
        <div class="grow">
          <div class="pos-label">${esc(label)}</div>
          <select class="input card-select" name="card_${index}">${opts}</select>
        </div>
        <label class="rev-toggle" title="Reversed?">
          <input type="checkbox" name="reversed_${index}" value="1">
          <svg class="ico" width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M4 9a8 8 0 0114-5l2 2M20 15a8 8 0 01-14 5l-2-2M17 4h3v3M7 20H4v-3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <span class="lbl up">Upright</span><span class="lbl rv">Reversed</span>
        </label>
      </div>`;

  let firstBuild = true;
  // Build the card-position rows for whatever spread (or custom build) is selected.
  function rebuildPositions() {
    if (!positionsEl) return;
    const opts = cardOptionsHTML(cards, currentSystem());
    const selected = (form.querySelector('input[name="spread_id"]:checked') as HTMLInputElement | null)?.value || '';
    let labels: string[];
    if (selected === '__custom__') {
      labels = Array.from(form.querySelectorAll<HTMLInputElement>('#custom-positions input[name="position_label"]'))
        .map((inp, i) => inp.value.trim() || `Position ${i + 1}`);
    } else {
      const sp = spreads.find((s) => s.id === selected);
      labels = sp ? sp.positions.map((p) => p.label) : [];
    }
    positionsEl.innerHTML = labels.map((label, i) => posRowHTML(label, i, opts)).join('');
    // Wire each select to its reverse toggle (rune merkstave/symmetric handling).
    const selects = Array.from(positionsEl.querySelectorAll('.card-select')) as unknown as HTMLSelectElement[];
    selects.forEach((sel) => {
      // On first build of an edit, restore the saved card + orientation.
      if (firstBuild) {
        const idx = sel.name.replace('card_', '');
        const saved = initial[idx];
        if (saved) {
          sel.value = String(saved.c);
          const box = sel.closest('.pos-row')?.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
          if (box) box.checked = !!saved.r;
        }
      }
      sel.addEventListener('change', () => refreshRune(sel));
      refreshRune(sel);
    });
    firstBuild = false;
  }

  // Show only spreads matching the chosen system; keep a valid selection.
  function applySystem() {
    const sys = currentSystem();
    let checkedStillVisible = false;
    form.querySelectorAll<HTMLElement>('#spread-list .option').forEach((opt) => {
      const radio = opt.querySelector('input[name="spread_id"]') as HTMLInputElement | null;
      if (!radio) return;
      const optSys = opt.getAttribute('data-system');
      const visible = optSys === null || optSys === sys; // custom option has no data-system
      opt.style.display = visible ? '' : 'none';
      if (radio.checked && !visible) radio.checked = false;
      if (radio.checked && visible) checkedStillVisible = true;
    });
    if (!checkedStillVisible) {
      const first = form.querySelector(`#spread-list .option[data-system="${sys}"] input[name="spread_id"]`) as HTMLInputElement | null;
      if (first) first.checked = true;
    }
    syncCustomBuilder();
    rebuildPositions();
  }

  // Custom-spread builder ------------------------------------------------------
  const builder = document.getElementById('custom-spread-builder');
  const customPositions = document.getElementById('custom-positions');

  function syncCustomBuilder() {
    const isCustom = (form.querySelector('input[name="spread_id"]:checked') as HTMLInputElement | null)?.value === '__custom__';
    if (builder) builder.hidden = !isCustom;
  }
  function wireRemoveButtons() {
    form.querySelectorAll<HTMLButtonElement>('.custom-pos-remove').forEach((btn) => {
      btn.onclick = () => {
        const rows = form.querySelectorAll('.custom-pos-row');
        if (rows.length <= 1) return; // keep at least one
        btn.closest('.custom-pos-row')?.remove();
        rebuildPositions();
      };
    });
  }
  document.getElementById('custom-pos-add')?.addEventListener('click', () => {
    if (!customPositions) return;
    const n = customPositions.querySelectorAll('.custom-pos-row').length + 1;
    const div = document.createElement('div');
    div.className = 'row custom-pos-row';
    div.innerHTML = `<input class="input grow" type="text" name="position_label" placeholder="Position ${n} label">
      <button type="button" class="btn quiet sm custom-pos-remove" aria-label="Remove position">
        <svg class="ico" width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg></button>`;
    customPositions.appendChild(div);
    wireRemoveButtons();
    rebuildPositions();
  });
  customPositions?.addEventListener('input', rebuildPositions);
  wireRemoveButtons();

  form.querySelectorAll<HTMLInputElement>('input[name="spread_id"]').forEach((radio) => {
    radio.addEventListener('change', () => { if (radio.checked) { syncCustomBuilder(); rebuildPositions(); } });
  });
  form.querySelectorAll<HTMLInputElement>('input[name="system"]').forEach((radio) => {
    radio.addEventListener('change', () => { if (radio.checked) applySystem(); });
  });

  // Reveal the "new deck" name input only when "Add a new deck…" is chosen.
  const newDeck = document.getElementById('new-deck') as HTMLInputElement | null;
  form.querySelectorAll<HTMLInputElement>('input[name="deck_id"]').forEach((radio) => {
    radio.addEventListener('change', () => {
      if (!radio.checked || !newDeck) return;
      newDeck.hidden = radio.value !== '__new__';
      if (radio.value === '__new__') newDeck.focus();
    });
  });

  // Reveal the querent name input only when "Someone else" is chosen.
  const querentName = document.getElementById('querent-name') as HTMLInputElement | null;
  form.querySelectorAll<HTMLInputElement>('input[name="querent"]').forEach((radio) => {
    radio.addEventListener('change', () => {
      if (!radio.checked || !querentName) return;
      querentName.hidden = radio.value !== 'other';
      if (radio.value === 'other') querentName.focus();
    });
  });

  // remind: focus the date when "custom" is chosen
  const remindDate = document.getElementById('remind-date') as HTMLInputElement | null;
  form.querySelectorAll<HTMLInputElement>('input[name="remind"]').forEach((r) => {
    r.addEventListener('change', () => { if (r.checked && r.value === 'custom' && remindDate) remindDate.focus(); });
  });

  applySystem();
  setupPhoto(form);
}

function setupPhoto(form: HTMLFormElement) {
  const input = document.getElementById('photo-input') as HTMLInputElement | null;
  const mount = document.getElementById('photo-straighten') as HTMLElement | null;
  const hidden = document.getElementById('photo-id') as HTMLInputElement | null;
  if (!input || !mount || !hidden) return;
  const inp = input, mnt = mount, hid = hidden;
  const actions = document.getElementById('photo-actions') as HTMLElement | null;
  const camBtn = document.getElementById('cam-btn');
  const csrf = (form.querySelector('input[name="_csrf"]') as HTMLInputElement)?.value || '';
  const hideActions = () => { if (actions) actions.style.display = 'none'; };
  const showActions = () => { if (actions) actions.style.display = ''; };

  async function uploadBlob(blob: Blob, w: number, h: number) {
    const fd = new FormData();
    fd.append('photo', blob, 'spread.jpg');
    fd.append('_csrf', csrf);
    fd.append('width', String(w));
    fd.append('height', String(h));
    try {
      const r = await fetch('/photos', { method: 'POST', body: fd });
      const data = await r.json() as { photo_id?: string };
      if (data.photo_id) { hid.value = data.photo_id; showDone(); }
      else reset('Upload failed — try again.');
    } catch { reset('Upload failed — try again.'); }
  }
  // Straighten then upload — shared by file-upload and camera capture.
  function processFile(file: Blob) {
    hideActions();
    inp.disabled = true;
    mountStraighten(mnt, file, (res) => uploadBlob(res.blob, res.width, res.height), () => reset());
  }
  function showDone() {
    mnt.hidden = false;
    mnt.innerHTML = `<div class="row" style="gap:12px;background:var(--surface);border:1px solid var(--line);border-radius:16px;padding:12px;margin-top:4px">
      <span style="color:var(--brand)"><svg class="ico" width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M5 12l4.5 4.5L19 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
      <div class="grow"><div style="font-weight:600;font-size:14px">Straightened &amp; attached</div><div class="muted" style="font-size:12.5px">Perspective corrected before upload</div></div>
      <button type="button" class="btn quiet sm" id="photo-redo">Redo</button></div>`;
    document.getElementById('photo-redo')?.addEventListener('click', () => reset());
  }
  function reset(msg?: string) {
    inp.disabled = false; inp.value = ''; hid.value = '';
    mnt.hidden = true; mnt.innerHTML = '';
    showActions();
    if (msg) alert(msg);
  }

  inp.addEventListener('change', () => { const f = inp.files && inp.files[0]; if (f) processFile(f); });
  camBtn?.addEventListener('click', () => { hideActions(); openCamera(mnt, (blob) => processFile(blob), () => reset()); });
}

// ── Rune formula builder ────────────────────────────────────────────────────
function setupFormulaBuilder() {
  const form = document.getElementById('formula-form') as HTMLFormElement | null;
  if (!form) return;
  const glyphsEl = document.getElementById('formula-glyphs')!;
  const namesEl = document.getElementById('formula-names')!;
  const emptyEl = document.getElementById('formula-empty')!;
  const hidden = document.getElementById('formula-runes') as HTMLInputElement;
  const pop = document.getElementById('formula-pop') as HTMLButtonElement;
  const seq: { name: string; glyph: string }[] = [];
  // name → glyph lookup, for prefilling an edited formula's sequence.
  const glyphByName = new Map<string, string>();
  try { for (const r of JSON.parse(form.dataset.runes || '[]')) glyphByName.set(r.name, r.glyph); } catch {}

  function render() {
    glyphsEl.textContent = seq.map((s) => s.glyph).join('');
    namesEl.textContent = seq.map((s) => s.name).join(' · ');
    hidden.value = JSON.stringify(seq.map((s) => s.name));
    emptyEl.style.display = seq.length ? 'none' : '';
    pop.hidden = seq.length === 0;
  }
  form.querySelectorAll<HTMLButtonElement>('.rune-pick').forEach((btn) => {
    btn.addEventListener('click', () => {
      seq.push({ name: btn.dataset.name || '', glyph: btn.dataset.glyph || '' });
      render();
    });
  });
  pop.addEventListener('click', () => { seq.pop(); render(); });
  // Prefill from an existing formula (edit mode).
  try {
    for (const name of JSON.parse(form.dataset.initialRunes || '[]')) seq.push({ name, glyph: glyphByName.get(name) || '·' });
  } catch {}
  render();
}

// ── Library spread builder: dynamic "+ add position" rows (no limit) ────────
function setupSpreadBuilder() {
  const list = document.getElementById('lib-positions');
  const add = document.getElementById('lib-pos-add');
  if (!list || !add) return;
  const removeIco = '<svg class="ico" width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
  const wireRemoves = () => list.querySelectorAll<HTMLButtonElement>('.custom-pos-remove').forEach((b) => {
    b.onclick = () => { if (list.querySelectorAll('.custom-pos-row').length > 1) { b.closest('.custom-pos-row')?.remove(); } };
  });
  add.addEventListener('click', () => {
    const n = list.querySelectorAll('.custom-pos-row').length + 1;
    const row = document.createElement('div');
    row.className = 'row custom-pos-row';
    row.innerHTML = `<input class="input grow" type="text" name="position_label" placeholder="Position ${n}">
      <button type="button" class="btn quiet sm custom-pos-remove" aria-label="Remove position">${removeIco}</button>`;
    list.appendChild(row);
    wireRemoves();
  });
  wireRemoves();
}

// ── Ritual form: dynamic materials + steps rows ──────────────────────────────
function setupRitualForm() {
  const removeIco = '<svg class="ico" width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
  function wireList(listId: string, addId: string, name: string, placeholder: (n: number) => string) {
    const list = document.getElementById(listId);
    const add = document.getElementById(addId);
    if (!list || !add) return;
    const wireRemoves = () => list.querySelectorAll<HTMLButtonElement>('.custom-pos-remove').forEach((b) => {
      b.onclick = () => b.closest('.custom-pos-row')?.remove();
    });
    add.addEventListener('click', () => {
      const n = list.querySelectorAll('.custom-pos-row').length + 1;
      const row = document.createElement('div');
      row.className = 'row custom-pos-row';
      row.innerHTML = `<input class="input grow" type="text" name="${esc(name)}" placeholder="${esc(placeholder(n))}">
        <button type="button" class="btn quiet sm custom-pos-remove" aria-label="Remove">${removeIco}</button>`;
      list.appendChild(row);
      wireRemoves();
      (row.querySelector('input') as HTMLInputElement)?.focus();
    });
    wireRemoves();
  }
  wireList('ritual-needs', 'need-add', 'material', (n) => `Item ${n}`);
  wireList('ritual-steps', 'step-add', 'step', (n) => `Step ${n}`);
}

document.addEventListener('DOMContentLoaded', () => { setupReadingForm(); setupFormulaBuilder(); setupSpreadBuilder(); setupRitualForm(); setupPWA(); });
